import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ── Data types ──────────────────────────────────────────────────────────────

export type SignalCategory = 'trade' | 'market';

export type StrategySignalData = {
  symbol: string;
  strategyId: string;
  direction: 'buy' | 'sell' | 'hold';
  compositeScore: number;
  confidence: number;
  reasons: string[];
  timeframeScores: Record<string, number>;
  evaluatedAt: number;
  warmupComplete: boolean;
  /** 'trade' = actionable (above threshold), 'market' = informational */
  category?: SignalCategory | undefined;
};

export type PaperTradeData = {
  tradeId: string;
  /**
   * Owning paper portfolio. The gateway always tags trades with this; it lets
   * the UI scope a trade feed to the selected wallet so one wallet's trades
   * never leak into another's view (the ghost-trade bug).
   */
  portfolioId?: string | undefined;
  symbol: string;
  strategyId: string;
  action: 'open' | 'update' | 'close';
  side: 'long' | 'short';
  entryPrice: number;
  stopLossPrice: number;
  takeProfitPrice: number;
  positionSize: number;
  positionQty: number;
  exitPrice?: number | undefined;
  exitReason?: string | undefined;
  realizedPnl?: number | undefined;
  realizedPnlPct?: number | undefined;
  openedAt: number;
  closedAt?: number | undefined;
  /** Market type — SPOT today; reserved for MARGIN/FUTURES later. */
  marketType?: 'SPOT' | 'MARGIN' | 'FUTURES' | undefined;
  /** True for operator-held MANUAL trades — scopes the trade history to the
   *  Manual sleeve (vs Strategy). Absent ⇒ engine-managed. */
  manual?: boolean | undefined;
};

export type PortfolioData = {
  cashBalance: number;
  deployedCapital: number;
  totalEquity: number;
  unrealizedPnl: number;
  realizedPnl: number;
  openPositionCount: number;
  totalTradesCount: number;
  winCount: number;
  lossCount: number;
  winRate: number;
  profitFactor: number;
  maxDrawdownPct: number;
  positions: Array<{
    symbol: string;
    side: string;
    strategyId: string;
    entryPrice: number;
    currentPrice: number;
    stopLossPrice: number;
    takeProfitPrice: number;
    unrealizedPnl: number;
    // Optional — present when the strategy-engine publishes them.
    tradeId?: string | undefined;
    positionQty?: number | undefined;
    positionSize?: number | undefined;
    openedAt?: number | undefined;
    marketType?: 'SPOT' | 'MARGIN' | 'FUTURES' | undefined;
    isBasket?: boolean | undefined;
    manual?: boolean | undefined;
    /** Manual hold entry provenance: 'kraken-vwap'/'manual' ⇒ real cost basis
     *  (show +/-); 'adoption'/absent ⇒ placeholder (prompt to set cost basis). */
    entrySource?: 'kraken-vwap' | 'manual' | 'adoption' | undefined;
    // Rotation proximity (basket only; as-of-last-rebalance).
    rotationRank?: number | undefined;
    rotationTopK?: number | undefined;
    rotationRegimeClosed?: boolean | undefined;
    onBubble?: boolean | undefined;
    nextRebalanceTs?: number | undefined;
  }>;
};

// ── Strategy metadata ─────────────────────────────────────────────────────

export type StrategyInfo = {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  warmupComplete: boolean;
  signalCount: number;
  tradeCount: number;
  winCount: number;
  lossCount: number;
  totalPnl: number;
  openPositions: number;
  allocatedBalance: number;
  returnPct: number;
};

// ── Constants ─────────────────────────────────────────────────────────────

/** Default strategy list — starts empty; populated at runtime from signals/API. */
export const DEFAULT_STRATEGIES: StrategyInfo[] = [];

// ── Store ───────────────────────────────────────────────────────────────────

const MAX_SIGNALS = 200;
const MAX_TRADES = 200;

const EMPTY_SIGNALS: StrategySignalData[] = [];
const EMPTY_TRADES: PaperTradeData[] = [];

type StrategyState = {
  signals: StrategySignalData[];
  trades: PaperTradeData[];
  portfolio: PortfolioData | null;
  strategies: StrategyInfo[];
  version: number;
  lastUpdateAt: number | null;

  addSignal: (signal: StrategySignalData) => void;
  setSignals: (signals: StrategySignalData[]) => void;
  addTrade: (trade: PaperTradeData) => void;
  setTrades: (trades: PaperTradeData[]) => void;
  setPortfolio: (portfolio: PortfolioData) => void;
  /**
   * Drop the global trade feed. The store holds trades for a single selected
   * portfolio at a time; when the user switches wallets we must clear these
   * immediately so a wallet with no trades doesn't inherit the previous
   * wallet's trade feed. The gateway only sends a `paper-trade-snapshot` when
   * trades exist, so without this an empty wallet would keep showing the prior
   * wallet's "Recent Trades". We deliberately do NOT clear `portfolio` here:
   * the dashboard gates its spinner on `portfolio` being non-null, and the
   * per-portfolio snapshot push for an empty wallet can lag, so nulling it
   * leaves the page stuck on a spinner. Portfolio metrics are already correct
   * per-wallet (scoped by the subscription), so they don't leak.
   */
  clearPortfolioScopedData: () => void;
  setStrategies: (strategies: StrategyInfo[]) => void;

  // Strategy management
  toggleStrategy: (id: string) => void;
};

/** Infer signal category from direction and composite score. */
function inferCategory(s: StrategySignalData): SignalCategory {
  if (s.category) return s.category;
  if ((s.direction === 'buy' || s.direction === 'sell') && Math.abs(s.compositeScore) >= 0.5) {
    return 'trade';
  }
  return 'market';
}

/** Filter out pure noise (warmup hold signals with zero score). */
function isInteresting(s: StrategySignalData): boolean {
  if (!s.warmupComplete) return false;
  if (s.direction === 'hold' && Math.abs(s.compositeScore) < 0.1) return false;
  return true;
}

export const useStrategyStore = create<StrategyState>()(
  persist(
    (set, get) => ({
      signals: EMPTY_SIGNALS,
      trades: EMPTY_TRADES,
      portfolio: null,
      strategies: DEFAULT_STRATEGIES,
      version: 0,
      lastUpdateAt: null,

      addSignal: (signal) =>
        set((state) => {
          if (!isInteresting(signal)) return state;
          const enriched = { ...signal, category: inferCategory(signal) };
          return {
            signals: [enriched, ...state.signals].slice(0, MAX_SIGNALS),
            version: state.version + 1,
            lastUpdateAt: Date.now(),
          };
        }),

      setSignals: (signals) =>
        set(() => ({
          signals: signals.filter(isInteresting)
            .map((s) => ({ ...s, category: inferCategory(s) }))
            .slice(0, MAX_SIGNALS),
          version: 1,
          lastUpdateAt: Date.now(),
        })),

      addTrade: (trade) =>
        set((state) => ({
          trades: [trade, ...state.trades].slice(0, MAX_TRADES),
          version: state.version + 1,
          lastUpdateAt: Date.now(),
        })),

      setTrades: (trades) =>
        set(() => ({
          trades: trades.slice(0, MAX_TRADES),
          version: 1,
          lastUpdateAt: Date.now(),
        })),

      setPortfolio: (portfolio) =>
        set((state) => ({
          portfolio,
          version: state.version + 1,
          lastUpdateAt: Date.now(),
        })),

      clearPortfolioScopedData: () =>
        set((state) => ({
          trades: EMPTY_TRADES,
          version: state.version + 1,
          lastUpdateAt: Date.now(),
        })),

      setStrategies: (strategies) =>
        set((state) => ({
          strategies,
          version: state.version + 1,
        })),

      toggleStrategy: (id) =>
        set((state) => ({
          strategies: state.strategies.map((s) =>
            s.id === id ? { ...s, enabled: !s.enabled } : s,
          ),
          version: state.version + 1,
        })),

    }),
    {
      name: 'lynx-strategy-store',
      version: 3, // v3: aligned strategy IDs with engine
      partialize: (state) => ({
        strategies: state.strategies,
      }),
      migrate: () => {
        // v2: reset all cached strategy data — user's paper wallet balance is now authoritative
        return {
          strategies: DEFAULT_STRATEGIES,
          signals: [],
          trades: [],
          portfolio: null,
          version: 0,
          lastUpdateAt: 0,
        };
      },
    },
  ),
);
